import { PageKey } from '@core/enums/page-keys.enum';
import { TSAStatus } from '@core/enums/tsa-status.enum';
import { VisitStatus } from '@core/enums/visit-status.enum';
import { FlowId, type CheckinWorkflow } from '../workflow-engine.types';
import { covidAcknowledgementFlow } from './covid-acknowledgement.flow';
import { preCheckinFlow } from './pre-checkin.flow';
import { qrCodeValidationFlow } from './qr-code-validation.flow';
import { reportToReceptionFlow } from './report-to-reception.flow';

export enum ExistingQrCodeStep {
  Start = 'Start',
  AfterCovid = 'AfterCovid',
  ScanQr = 'ScanQr',
  AfterQrValidation = 'AfterQrValidation',
  AfterReception = 'AfterReception',
  AfterTsaReception = 'AfterTsaReception',
  AfterPreCheckin = 'AfterPreCheckin',
  AlreadyCheckedIn = 'AlreadyCheckedIn',
  NotPreCheckedIn = 'NotPreCheckedIn',
  SendNotPreCheckedInToReception = 'SendNotPreCheckedInToReception',
}

export const existingQrCodeFlow: CheckinWorkflow<ExistingQrCodeStep> = {
  id: FlowId.ExistingQrCode,
  entry: ExistingQrCodeStep.Start,
  steps: {
    [ExistingQrCodeStep.Start]: {
      id: ExistingQrCodeStep.Start,
      next: (variables) =>
        variables.checkinProcessConfiguration?.hasCoronavirusScreen
          ? { flow: covidAcknowledgementFlow, returnStep: ExistingQrCodeStep.AfterCovid }
          : { nextStep: ExistingQrCodeStep.ScanQr },
      back: () => ({ bubble: 'back' }),
      exit: () => ({ bubble: 'exit' }),
    },
    [ExistingQrCodeStep.AfterCovid]: {
      id: ExistingQrCodeStep.AfterCovid,
      next: () => ({ nextStep: ExistingQrCodeStep.ScanQr }),
    },
    [ExistingQrCodeStep.ScanQr]: {
      id: ExistingQrCodeStep.ScanQr,
      next: () => ({
        flow: qrCodeValidationFlow,
        returnStep: ExistingQrCodeStep.AfterQrValidation,
      }),
      back: (variables) =>
        variables.checkinProcessConfiguration?.hasCoronavirusScreen
          ? { nextStep: ExistingQrCodeStep.Start }
          : { bubble: 'back' },
      exit: () => ({ bubble: 'exit' }),
    },
    [ExistingQrCodeStep.AfterQrValidation]: {
      id: ExistingQrCodeStep.AfterQrValidation,
      next: (variables) => {
        if (variables.isVisitPrecheckedIn) {
          if (variables.visitStatus === VisitStatus.DRY_PRECHECKED_IN) {
            return { flow: preCheckinFlow, returnStep: ExistingQrCodeStep.AfterPreCheckin };
          }
          const configuration = variables.checkinProcessConfiguration;
          const tsaNotRequiredForTraining =
            configuration?.hasTsa === true &&
            variables.tsaStatus === TSAStatus.NOT_REQUIRED &&
            variables.trainingId != null;

          if (configuration?.hasTsa === false || tsaNotRequiredForTraining) {
            return { flow: preCheckinFlow, returnStep: ExistingQrCodeStep.AfterPreCheckin };
          }
          return {
            flow: reportToReceptionFlow,
            returnStep: ExistingQrCodeStep.AfterTsaReception,
          };
        }

        return variables.visitStatus === VisitStatus.IN || variables.visitStatus === VisitStatus.OUT
          ? { nextStep: ExistingQrCodeStep.AlreadyCheckedIn }
          : { nextStep: ExistingQrCodeStep.NotPreCheckedIn };
      },
      back: () => ({ nextStep: ExistingQrCodeStep.ScanQr }),
      exit: () => ({ bubble: 'exit' }),
    },
    [ExistingQrCodeStep.AfterTsaReception]: {
      id: ExistingQrCodeStep.AfterTsaReception,
      next: () => ({ flow: preCheckinFlow, returnStep: ExistingQrCodeStep.AfterPreCheckin }),
      back: () => ({ nextStep: ExistingQrCodeStep.ScanQr }),
      exit: () => ({ bubble: 'exit' }),
    },
    [ExistingQrCodeStep.AfterPreCheckin]: {
      id: ExistingQrCodeStep.AfterPreCheckin,
      next: () => ({ complete: true }),
    },
    [ExistingQrCodeStep.AlreadyCheckedIn]: {
      id: ExistingQrCodeStep.AlreadyCheckedIn,
      page: PageKey.ALREADY_CHECKEDIN,
      next: () => ({ complete: true }),
    },
    [ExistingQrCodeStep.NotPreCheckedIn]: {
      id: ExistingQrCodeStep.NotPreCheckedIn,
      page: PageKey.NOT_PRECHECKED_IN,
      next: () => ({ nextStep: ExistingQrCodeStep.SendNotPreCheckedInToReception }),
    },
    [ExistingQrCodeStep.SendNotPreCheckedInToReception]: {
      id: ExistingQrCodeStep.SendNotPreCheckedInToReception,
      next: () => ({ flow: reportToReceptionFlow, returnStep: ExistingQrCodeStep.AfterReception }),
      back: () => ({ nextStep: ExistingQrCodeStep.ScanQr }),
      exit: () => ({ bubble: 'exit' }),
    },
    [ExistingQrCodeStep.AfterReception]: {
      id: ExistingQrCodeStep.AfterReception,
      next: () => ({ complete: true }),
      back: () => ({ nextStep: ExistingQrCodeStep.ScanQr }),
      exit: () => ({ bubble: 'exit' }),
    },
  },
};
